import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import {formatDateToDDMMYYYY, getAuthToken, getClientIdOfUser, showToast,} from "../../utils/utility";
import * as Constants from "../../utils/Constants";
import MapLink from "../../utils/MapLink";
import {CardList} from "../CardView";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFilePdf, faMapLocationDot, faStarOfLife,} from "@fortawesome/free-solid-svg-icons";
// Unified CSS
import "../../styles/components/tasks.css";
import ExportTrackingReport from "./ExportTrackingReport";

function TrackingReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reports, setReports] = useState({
    geofenceEvents: [],
    unassociatedLockUnlockReports: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});

  const trackingReportRef = useRef(null);

  // Local logout to handle 403 without circular dependency
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  useEffect(() => {
    fetchData()
      .then((data) => {
        if (Array.isArray(data)) {
          setAssetList(data);
        }
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  const fetchData = async () => {
    try {
      const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await axios.get(`${Constants.BASE_URL}${endPoint}`);
      if (response.status === 200) {
        return response.data.responseData;
      } else if (response.status === 403) {
        handleLogout();
      }
      return [];
    } catch (error) {
      handleLogout();
      return [];
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!validateInputs(checkedItems, startDate, endDate)) return;

      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;

      let apiUrl = `api/v1/${getClientIdOfUser()}/tracking-report`;
      const checkedAssetIds = Object.keys(checkedItems).filter((assetId) => checkedItems[assetId]);

      if (checkedAssetIds.length > 0) {
        apiUrl += `?assetId=${checkedAssetIds.join(",")}`;
      }

      apiUrl += `&startDate=${startDate} 00:00:00&endDate=${endDate} 23:59:59`;

      const response = await axios.get(Constants.BASE_URL + apiUrl);

      if (response.status === 200) {
        const responseData = response.data;
        setReports({
          geofenceEvents: responseData.responseData.reports || [],
          unassociatedLockUnlockReports: responseData.responseData.unassociatedLockUnlockReports || [],
        });
      } else if (response.status === 403) {
        handleLogout();
      }
    } catch (error) {
      console.error(error.message);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  function validateInputs(checkedItems, startDate, endDate) {
    if (Object.keys(checkedItems).length === 0 || !startDate || !endDate) {
      alert("Please select mandatory fields (*) [Device, Start Date & End Date] to generate the report.");
      return false;
    }
    if (parseDate(startDate) > parseDate(endDate)) {
      alert("Start Date should be earlier than or equal to the End Date.");
      return false;
    }
    return true;
  }

  function parseDate(dateString) {
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day);
  }

  const handleCheckboxToggle = (assetUnitId) => {
    setCheckedItems((prev) => ({ ...prev, [assetUnitId]: !prev[assetUnitId] }));
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setReports({ geofenceEvents: [], unassociatedLockUnlockReports: [] });
    setCheckedItems({});
    document.querySelectorAll(".filter-input").forEach(el => el.value = "");
  };

  const handleExportPDF = () => {
    if (reports.geofenceEvents.length === 0 && reports.unassociatedLockUnlockReports.length === 0) {
      showToast("No data available for export.", { autoClose: 3000, type: "error" });
      return;
    }

    const element = trackingReportRef.current;
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const fileName = `TrackingReport_${formattedDate}.pdf`;

    html2pdf(element, {
      margin: [0.3, 0.3],
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    });
  };

  // --- RENDER HELPERS ---

  const renderTrackingRow = (tracking) => {
    return (
      <React.Fragment key={tracking.customerId}>
        {/* HEADER */}
        <tr style={{ background: "#FF683A", color: "#fff" }}>
          <td colSpan={8} style={{ padding: "8px", fontWeight: "bold" }}>
            Customer Details
          </td>
        </tr>

        {/* CUSTOMER INFO */}
        <tr style={{background: '#fff'}}>
          <td colSpan={2} style={{padding:'8px'}}><strong>Customer Id:</strong> {tracking.customerId}</td>
          <td colSpan={3} style={{padding:'8px'}}><strong>Name:</strong> {tracking.customerName}</td>
          <td colSpan={3} style={{padding:'8px'}}><strong>Address:</strong> {tracking.customerAddress}</td>
        </tr>
        <tr style={{background: '#fff'}}>
          <td colSpan={2} style={{padding:'8px'}}><strong>Email:</strong> {tracking.email}</td>
          <td colSpan={3} style={{padding:'8px'}}><strong>Phone:</strong> {tracking.phoneNumber}</td>
          <td colSpan={3} style={{padding:'8px'}}>
             <strong>Location:</strong> <MapLink latitude={tracking.latitude} longitude={tracking.longitude} />
          </td>
        </tr>

        {/* SECTION HEADER */}
        <tr style={{ background: "#363168", color: "#fff" }}>
          <td colSpan={8} style={{ padding: "6px", fontWeight: "bold", fontSize: '13px' }}>
            Device Activity & Events
          </td>
        </tr>

        {/* ACTIVITY TABLES CONTAINER */}
        <tr>
          <td colSpan={8} style={{ padding: "0" }}>
            <div style={{ display: "flex", width: "100%" }}>
              
              {/* LEFT: Geofence Events */}
              <div style={{ flex: 1, borderRight: "1px solid #ccc" }}>
                <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f1f1f1" }}>
                      <th style={{padding:'4px', textAlign:'left'}}>Device Id</th>
                      <th style={{padding:'4px', textAlign:'left'}}>Event Type</th>
                      <th style={{padding:'4px', textAlign:'left'}}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracking.geofenceEvents.map((event, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{padding:'4px'}}>{event.assetId}</td>
                        <td style={{padding:'4px'}}><strong>{event.eventType}</strong></td>
                        <td style={{padding:'4px'}}>
                          {new Date(event.timestamp).toLocaleString("en-IN", { timeZone: "UTC" })}
                        </td>
                      </tr>
                    ))}
                    {tracking.geofenceEvents.length === 0 && (
                        <tr><td colSpan={3} style={{padding:'10px', textAlign:'center', color:'#999'}}>No device activity</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* RIGHT: Lock/Unlock Events */}
              <div style={{ flex: 1 }}>
                <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f1f1f1" }}>
                      <th style={{padding:'4px', textAlign:'left'}}>Report Id</th>
                      <th style={{padding:'4px', textAlign:'left'}}>Source</th>
                      <th style={{padding:'4px', textAlign:'left'}}>Time</th>
                      <th style={{padding:'4px', textAlign:'left'}}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracking.lockUnlockEvents.map((evt, index) => (
                      <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{padding:'4px'}}>{evt.reportSerialNumber}</td>
                        <td style={{padding:'4px'}}>{evt.eventSource}</td>
                        <td style={{padding:'4px'}}>
                          {new Date(evt.reportDateTime).toLocaleString("en-IN", { timeZone: "UTC" })}
                        </td>
                        <td style={{padding:'4px'}}>
                          <MapLink latitude={evt.latitude} longitude={evt.longitude} />
                        </td>
                      </tr>
                    ))}
                    {tracking.lockUnlockEvents.length === 0 && (
                        <tr><td colSpan={4} style={{padding:'10px', textAlign:'center', color:'#999'}}>No lock events</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

            </div>
          </td>
        </tr>
        
        {/* SPACER ROW */}
        <tr><td colSpan={8} style={{height:'30px', border:'none'}}></td></tr>
      </React.Fragment>
    );
  };

  const renderTrackingTable = () => {
    return (
      <div>
        {reports.geofenceEvents.map((report, reportIndex) => (
          <div key={reportIndex} className="tracking-report-container">
            <div style={{ 
              marginBottom: "10px", 
              fontSize: '15px', 
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FontAwesomeIcon icon={faMapLocationDot} style={{color: '#FF683A'}} /> 
              Location - {reportIndex + 1}
            </div>
            {/* The main table wrapper for this report item */}
            <table className="report-html-table" style={{ width: "100%" }}>
              <tbody>{renderTrackingRow(report)}</tbody>
            </table>
          </div>
        ))}

        {reports.unassociatedLockUnlockReports.length > 0 && (
          <div className="tracking-report-container" style={{ marginTop: "20px" }}>
            <h4 style={{ 
              fontSize: '15px', 
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px'
            }}>
              <FontAwesomeIcon icon={faStarOfLife} style={{color: '#FF683A'}} /> 
              Unassociated Lock/Unlock Events
            </h4>
            <table className="report-html-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Report Id</th>
                  <th>Event Source</th>
                  <th>Time</th>
                  <th>Location Link</th>
                </tr>
              </thead>
              <tbody>
                {reports.unassociatedLockUnlockReports.map((event, index) => (
                  <tr key={index}>
                    <td>{event.reportSerialNumber}</td>
                    <td>{event.eventSource}</td>
                    <td>
                      {new Date(event.reportDateTime).toLocaleString("en-IN", { timeZone: "UTC" })}
                    </td>
                    <td>
                      <MapLink latitude={event.latitude} longitude={event.longitude} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="report-content-split">
      
      {/* MAIN AREA */}
      <div className="report-main-area">
        <div className="report-header-actions">
          <span style={{ fontSize: "15px", fontWeight: 700, color: "#1e293b" }}>
            Asset Tracking Results
          </span>
          {(reports.geofenceEvents.length > 0 || reports.unassociatedLockUnlockReports.length > 0) && (
            <div className="export-group">
              <ExportTrackingReport data={reports} />
              <button className="btn-export" onClick={handleExportPDF}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        <div className="report-table-wrapper" ref={trackingReportRef}>
          {reports.geofenceEvents.length > 0 || reports.unassociatedLockUnlockReports.length > 0 ? (
            renderTrackingTable()
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
              <p>Select devices and date range to generate tracking report.</p>
            </div>
          )}
        </div>
      </div>

      {/* FILTER SIDEBAR */}
      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>

        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">Select Devices <span style={{color: '#FF683A'}}>*</span></label>
            <div className="asset-list-wrapper">
              <CardList
                data={assetList}
                checkedItems={checkedItems}
                toggleCheckbox={handleCheckboxToggle}
              />
            </div>
          </div>

          <div >
            <label className="filter-label">Start Date <span style={{color: '#FF683A'}}>*</span></label>
            <input
              type="date"
              className="filter-input"
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setStartDate(formatDateToDDMMYYYY(e.target.value))}
            />
          </div>

          <div >
            <label className="filter-label">End Date <span style={{color: '#FF683A'}}>*</span></label>
            <input
              type="date"
              className="filter-input"
              onChange={(e) => setEndDate(formatDateToDDMMYYYY(e.target.value))}
            />
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={handleSearch} disabled={isLoading}>
              {isLoading ? "Generating..." : Constants.FETCH_REPORTS}
            </button>
            <button className="btn-report secondary" onClick={handleClear}>
              {Constants.CLEAR_FILTERS}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrackingReport;